import { updateCompanySpecificIndustryApi, useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { TGetCompanySpecificIndustryListOutputDto } from "@/api/services/global/master/dtos/companySpecificIndustry/get-master-company-specific-industry-list.dto";
import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { BaselineEllipsisFontFace } from "@/features/master-data/components/CompanySpecificIndustry/EllipsisText";
import {
  CHECKBOX_COLUMN_WIDTH,
  CompanySpecificIndustryRow,
} from "@/features/master-data/components/CompanySpecificIndustry/CompanySpecificIndustryRow";
import { CreateCompanySpecificIndustryFormModal } from "@/features/master-data/components/CompanySpecificIndustry/CreateCompanySpecificIndustryFormModal";
import { HiddenCompanySpecificIndustryModal } from "@/features/master-data/components/CompanySpecificIndustry/HiddenCompanySpecificIndustryModal";
import { UpdateCompanySpecificIndustryFormModal } from "@/features/master-data/components/CompanySpecificIndustry/UpdateCompanySpecificIndustryFormModal";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { getRowTogglingKey, useCompanySpecificIndustryVisaFlags } from "@/features/master-data/hooks/useCompanySpecificIndustryVisaFlags";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { background, button, divider, green } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { Box, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { Link } from "react-router-dom";

export const MasterDataCompanySpecificIndustry = () => {
  const createCompanySpecificIndustryFormModal = useVisible();
  const updateCompanySpecificIndustryFormModal = useVisible();
  const [enableSaveOrderBtn, setEnableOrderBtn] = useState<boolean>(false);
  const [hiddenCompanySpecificIndustry, setHiddenCompanySpecificIndustry] = useState<TGetCompanySpecificIndustryListOutputDto>();
  const [selectedCompanySpecificIndustry, setSelectedCompanySpecificIndustry] = useState<TGetCompanySpecificIndustryListOutputDto>();

  const { data, isLoading } = useGetCompanySpecificIndustryList();

  const [rows, setRows] = useState<TGetCompanySpecificIndustryListOutputDto[]>(data);
  const { toggleSpecificSkill, toggleEmploymentTraining, togglingKeys } = useCompanySpecificIndustryVisaFlags(setRows);

  useEffect(() => {
    if (!data?.length) {
      return;
    }
    if (!enableSaveOrderBtn) {
      setRows(data);
      return;
    }
    // Unsaved drag order: take fresh data but keep the order the user arranged.
    setRows((prev) => {
      const orderOf = (id: number) => {
        const index = prev.findIndex((row) => row.id === id);
        return index === -1 ? prev.length : index;
      };
      return [...data].sort((a, b) => orderOf(a.id) - orderOf(b.id));
    });
  }, [data]);

  // Reflect the toggle immediately; the refetch only confirms it. Enabling leaves the classifications as they are.
  const handleHiddenChanged = (id: number, isHidden: boolean) =>
    setRows((prev) =>
      prev.map((row) =>
        toNumber(row.id) === id
          ? {
              ...row,
              isHidden,
              companySpecificBusinessClassifications: isHidden
                ? row.companySpecificBusinessClassifications.map((item) => ({ ...item, isHidden: true }))
                : row.companySpecificBusinessClassifications,
            }
          : row,
      ),
    );

  const { open: openUpdateModal } = updateCompanySpecificIndustryFormModal;

  const handleEdit = useCallback(
    (row: TGetCompanySpecificIndustryListOutputDto) => {
      setSelectedCompanySpecificIndustry(row);
      openUpdateModal();
    },
    [openUpdateModal],
  );

  const handleSaveOrder = async () => {
    try {
      const rs = await updateCompanySpecificIndustryApi({
        companySpecificIndustries: rows.flatMap((row) => ({
          id: toNumber(row.id),
          // Only the order is saved here; names are omitted so stale rows can't overwrite them.
          companySpecificBusinessClassifications: row.companySpecificBusinessClassifications.map((csbc) => ({
            id: toNumber(csbc.id),
          })),
        })),
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setEnableOrderBtn(false);
    } catch (error) {
      showError(error);
    }
  };

  // Handle row dragging
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !result.source || result.source.index === result.destination.index) {
      return;
    }
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    setRows(reorderedRows);
    setEnableOrderBtn(true);
  };

  if (isLoading || !rows.length) {
    return <LoadingPage />;
  }

  return (
    <>
      <BaselineEllipsisFontFace />
      <TopPage title={"「" + MASTER_DATA_TYPE_TITLE.COMPANY_SPECIFIC_INDUSTRY + "」の編集"} enablePageBack={false} />
      <InnerBox gap={3}>
        <Stack direction="row" justifyContent="flex-end" mb={3}>
          <Button size="sm" variant="primary" onClick={() => createCompanySpecificIndustryFormModal.open()}>
            項目追加
          </Button>
        </Stack>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="tableRows" direction="vertical">
            {(provided) => (
              <StyledTable {...provided.droppableProps} ref={provided.innerRef}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center !important",
                          whiteSpace: "normal !important",
                          width: CHECKBOX_COLUMN_WIDTH,
                          minWidth: CHECKBOX_COLUMN_WIDTH,
                        }}
                      >
                        特定技能
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center !important",
                          whiteSpace: "normal !important",
                          width: CHECKBOX_COLUMN_WIDTH,
                          minWidth: CHECKBOX_COLUMN_WIDTH,
                        }}
                      >
                        育成就労
                      </TableCell>
                      <TableCell>特定産業分野（企業）</TableCell>
                      <TableCell>業務区分（会社情報用）</TableCell>
                      <TableCell>主たる技能</TableCell>
                      <TableCell>加入している分野別協議会</TableCell>
                      <TableCell>試験名（1年経過時まで）</TableCell>
                      <TableCell>級（1年経過時まで）</TableCell>
                      <TableCell>試験名（育成就労終了まで）</TableCell>
                      <TableCell>級（育成就労終了まで）</TableCell>
                      {/* The header is much longer than its values (1年 / 2年), so it wraps instead of widening the column. */}
                      <TableCell sx={{ whiteSpace: "normal !important" }}>育成就労実施者の変更を制限する期間(分野単位)</TableCell>
                      <TableCell>最終更新日</TableCell>
                      <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "100px", bgcolor: background.white }}>
                        有効／無効
                      </TableCell>
                      <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "0", bgcolor: background.white }}>編集</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                        {(provided) => (
                          <CompanySpecificIndustryRow
                            row={row}
                            provided={provided}
                            togglingKey={getRowTogglingKey(row, togglingKeys)}
                            onToggleSpecificSkill={toggleSpecificSkill}
                            onToggleEmploymentTraining={toggleEmploymentTraining}
                            onChangeHidden={setHiddenCompanySpecificIndustry}
                            onEdit={handleEdit}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </StyledTable>
            )}
          </Droppable>
        </DragDropContext>
      </InnerBox>
      <FooterActionBar mt={4}>
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Button component={Link} to={AppRoutes.masterData} variant="ghost" size="sm">
            戻る
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleSaveOrder()} disabled={!enableSaveOrderBtn}>
            オーダーを保存する
          </Button>
        </Stack>
      </FooterActionBar>
      {createCompanySpecificIndustryFormModal.visible && (
        <CreateCompanySpecificIndustryFormModal
          onClose={createCompanySpecificIndustryFormModal.close}
          isOpen={createCompanySpecificIndustryFormModal.visible}
        />
      )}

      {updateCompanySpecificIndustryFormModal.visible && (
        <UpdateCompanySpecificIndustryFormModal
          onClose={updateCompanySpecificIndustryFormModal.close}
          isOpen={updateCompanySpecificIndustryFormModal.visible}
          selectedCompanySpecificIndustry={selectedCompanySpecificIndustry}
        />
      )}

      <HiddenCompanySpecificIndustryModal
        hiddenCompanySpecificIndustry={hiddenCompanySpecificIndustry}
        setHiddenCompanySpecificIndustry={setHiddenCompanySpecificIndustry}
        onHiddenChanged={handleHiddenChanged}
      />
      <ConfirmLeavePage shouldBlock={enableSaveOrderBtn}></ConfirmLeavePage>
    </>
  );
};

const StyledTable = styled(Box)`
  border-radius: ${rounded.sm};
  overflow: auto;
  max-height: calc(100vh - 380px);
  border-top: 1px solid ${divider.middle};
  border-bottom: 1px solid ${divider.middle};
  border-right: 1px solid ${divider.middle};
  background-color: ${background.white};
  table {
    border-collapse: separate;
  }
  th,
  td {
    border-left: 1px solid ${divider.middle};
    border-bottom: 1px solid ${divider.middle};
  }
  * {
    box-shadow: none;
  }
  .MuiToolbar-dense {
    display: none;
  }
  .MuiTableCell-head {
    padding: 8px !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    white-space: nowrap;
    position: sticky;
    background-color: white;
    z-index: 500;
    top: 0;
  }

  .Mui-TableHeadCell-Content-Labels {
    > span {
      margin-left: 4px;
      img {
        opacity: 0.2;
      }
      .MuiBadge-badge {
        display: none;
      }
    }
  }
  .MuiTableCell-body {
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }

  // Default
  .MuiTableRow-root {
    background-color: ${background.white};
    box-shadow: none;
    cursor: initial;
    .MuiTableCell-root {
      padding: 0;
      border-color: ${divider.middle};
      opacity: 1;
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  // Hover
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }
  // Click/Pressed
  .Mui-selected {
    background-color: ${background.white} !important;
    td::after {
      background: ${background.secondary}!important;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  // The cells hold label-less checkboxes; drop the label gap so the box sits in the middle of its column.
  .MuiFormControlLabel-root {
    gap: 0;
  }

  .MuiCheckbox-root {
    color: ${button.primary}!important;
    height: 24px;
    width: 24px;
    padding: 0;
  }

  // Disabled Row
  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  // added row
  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.failed {
    background: ${background.error}!important;
    &::after {
      background: unset !important;
    }
  }

  // Clickable row
  .MuiTableCell-root.clickable {
    cursor: pointer;
  }
`;
